<template>
    <div class="admin-dashboard">
        <div class="TriangleTop">
            <div class="TriangleVerticalTop">
                <div class="logo-container">
                    <img class="logo" src="../assets/logo_wTVision_white.png" alt="Logo" @click="goHome">
                </div>
            </div>
            <div class="TriangleVerticalLine"></div>
        </div>
        <div class="BottomContainer">
            <h2>Admin Dashboard</h2>
        
            <div class="approvals">
                <h3>Pending User Approvals</h3>
                <ul>
                <li v-for="user in pendingUsers" :key="user.user_id">
                    {{ user.username }} ({{ user.email }})
                    <button @click="approveUser(user.user_id)">Approve</button>
                    <button @click="declineUser(user.user_id)">Decline</button>
                </li>
                </ul>
            </div>
        
            <div class="users">
                <h3>All Users</h3>
                <table>
                <thead>
                    <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Active</th>
                    <th>Admin</th>
                    <th>Reviewer</th>
                    <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in allUsers" :key="user.user_id">
                    <td>{{ user.username }}</td>
                    <td>{{ user.email }}</td>
                    <td>{{ user.isapproved ? 'Yes' : 'No' }}</td>
                    <td>{{ user.isadmin ? 'Yes' : 'No' }}</td>
                    <td>{{ user.isreviewer ? 'Yes' : 'No' }}</td>
                    <td>
                        <button @click="toggleUserActive(user)" :class="{'disable':user.username === currentUser?.username}">{{ user.isapproved ? 'Deactivate' : 'Activate' }}</button>
                        <button @click="deleteUser(user.user_id)" :class="{'disable':user.username === currentUser?.username}">Delete</button>
                        <button v-if="user.isapproved" @click="toggleUserAdmin(user)" :class="{'disable':user.username === currentUser?.username}">{{ user.isadmin ? 'Revoke Admin' : 'Make Admin' }}</button>
                        <button v-if="user.isapproved" @click="toggleUserReviewer(user)" :class="{'disable':user.username===currentUser?.username}">{{ user.isreviewer ? 'Revoke Reviewer' : 'Make Reviewer' }}</button>
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>
        </div>
    </div>
  </template>
  
<script lang="ts">
    import { defineComponent, ref, onMounted } from "vue";
    import { useRouter } from "vue-router";
    import { supabase } from "@/supabase";
    
    interface User {
        user_id: string;
        username: string;
        email: string;
        isapproved: boolean;
        isadmin: boolean;
        isreviewer: boolean;
    }
  
    export default defineComponent({
        name: 'AdminDashboard',
        setup() {
            const router = useRouter();
            const pendingUsers = ref<User[]>([]);
            const allUsers = ref<User[]>([]);
            const currentUser = ref<{ isadmin: boolean; username: string } | null>(null);
  
            const fetchPendingUsers = async () => {
                try {
                    const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('isapproved', false)
                    if (!error && data) {
                    pendingUsers.value = data
                    }
                } catch (error) {
                    console.error('Error fetching pending users:', error)
                }
            };
  
            const fetchAllUsers = async () => {
                try {
                    const { data, error } = await supabase.from('profiles').select('*')
                    if (!error && data) {
                    allUsers.value = data
                    }
                } catch (error) {
                    console.error('Error fetching all users:', error)
                }
            };

            const approveUser = async (userId: string) => {
                try {
                    await supabase
                    .from('profiles')
                    .update({ isapproved: true })
                    .eq('user_id', userId)

                    // Remove da lista de pending
                    pendingUsers.value = pendingUsers.value.filter((u) => u.user_id !== userId)

                    // Atualiza allUsers
                    fetchAllUsers()
                } catch (error) {
                    console.error('Error approving user:', error)
                }
            };
  
            const declineUser = async (userId: string) => {
                try {
                    const { error } = await supabase
                                            .from('profiles')
                                            .update({ isapproved: false })
                                            .eq('user_id', userId);

                    if (error) throw error;

                    // Remove da lista de pending
                    pendingUsers.value = pendingUsers.value.filter((u) => u.user_id !== userId)

                    // Atualiza allUsers
                    await fetchAllUsers()
                } catch (error) {
                    console.error('Error approving user:', error)
                    alert('Failed to decline user.');
                }
            };

            const toggleUserActive = async (user: User) => {
                try {
                    await supabase
                    .from('profiles')
                    .update({ isapproved: !user.isapproved })
                    .eq('user_id', user.user_id)

                    user.isapproved = !user.isapproved
                } catch (error) {
                    console.error('Error toggling user active status:', error)
                }
            };

            const toggleUserAdmin = async (user: User) => {
                try {
                    await supabase
                    .from('profiles')
                    .update({ isadmin: !user.isadmin })
                    .eq('user_id', user.user_id)

                    user.isadmin = !user.isadmin
                } catch (error) {
                    console.error('Error toggling user as admin:', error)
                }
            };

            const deleteUser = async (userId: string) => {
                if (confirm('Are you sure you want to delete this user?')) {
                    const { data, error } = await supabase.functions.invoke('delete-user', {
                        body: { user_id: userId },
                    });
                    
                    if (error) {
                        console.error("Error deleting user:", error);
                    } else {
                        await fetchAllUsers();
                    }
                }
            };

            const toggleUserReviewer = async (user: User) => {
                try {
                    await supabase.from('profiles')
                    .update({ isreviewer: !user.isreviewer })
                    .eq('user_id', user.user_id);
                    user.isreviewer = !user.isreviewer;
                } catch (e) {
                    console.error('Error toggling reviewer:', e);
                }
            };

            const goHome = () => {
                router.push('/');
            };
  
            onMounted(async() => {
                const { data: { session } } = await supabase.auth.getSession();
                const user = session?.user ?? null;
                if (!user) return router.push('/');

                const { data: profile, error } = await supabase
                                                        .from('profiles')
                                                        .select('isadmin, username')
                                                        .eq('user_id', user.id)
                                                        .single();

                if (error || !profile?.isadmin) {
                    router.push('/');
                    return;
                }

                currentUser.value = { isadmin: profile.isadmin, username: profile.username };

                await Promise.all([fetchPendingUsers(), fetchAllUsers()]);
            });
  
            return { pendingUsers, approveUser, declineUser, allUsers, toggleUserActive, toggleUserAdmin, deleteUser,
                     goHome, currentUser, toggleUserReviewer };
        },
    });
</script>
  
<style scoped>
    .admin-dashboard {
        width: 100vw;
    min-height: 100vh;
    background: #151515;
    color: white;
    }

    .TriangleTop {
        z-index: 3;
        --s: 9px; /* control the size*/
        --c1: #070707;
        --c2: #1a1a1a;
  
        --c:#00000000,var(--c1) .5deg 119.5deg,#0000 120deg;
        --g1:conic-gradient(from  60deg at 56.25% calc(425%/6),var(--c));
        --g2:conic-gradient(from 180deg at 43.75% calc(425%/6),var(--c));
        --g3:conic-gradient(from -60deg at 50%   calc(175%/12),var(--c));
        background:
            var(--g1),var(--g1) var(--s) calc(1.73*var(--s)),
            var(--g2),var(--g2) var(--s) calc(1.73*var(--s)),
            var(--g3) var(--s) 0,var(--g3) 0 calc(1.73*var(--s)) 
            var(--c2);
        background-size: calc(2*var(--s)) calc(3.46*var(--s));
    }

    .logo-container {
        padding-top: 50px;
        padding-left: 120px;
        display: inline-block;
        background: inherit;
        border-radius: 10px;
    }

    .logo {
        display: block;
        width: 60%;
        height: auto;
        -webkit-filter: drop-shadow(5px 5px 5px #707070);
        filter: drop-shadow(5px 5px 5px #707070);
        cursor: pointer;
    }

    .TriangleVerticalTop{
        width: 400px;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        transform: skew(-17deg) translateX(-50%);
        background: inherit;
    }
    
    .TriangleVerticalLine{
        width: 5px;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        transform: skew(-17deg) translateX(190px);
        background-color: rgb(190, 190, 190);
        box-shadow: 0 0 5px white, 0 0 5px white, 0 0 15px white;
    }

    .BottomContainer {
        display: block;
        width: 70%;
        background: inherit;
        color: white;
        padding: 20px;
        margin-left: 400px;
    }    
    
    .BottomContainer h2 {
        margin-bottom: 20px;
    }

    .BottomContainer h3 {
        margin-top: 30px;
    }

    .BottomContainer ul {
        list-style-type: none;
        padding: 0;
    }

    .BottomContainer li {
        margin-bottom: 10px;
    }

    .BottomContainer button {
        margin-left: 10px;
    }

    .BottomContainer table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 20px;
    }

    .BottomContainer th,
    .BottomContainer td {
        border: 1px solid #ddd;
        padding: 8px;
    }

    .BottomContainer th {
        background-color: #333;
        color: white;
    }

    .BottomContainer td {
        text-align: center;
    }

    .disable {
        pointer-events: none;
        background-color: grey;
    }
</style>
  